import {
  Flex,
  Box,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Link,
  Button,
  Heading,
  Text,
  useColorModeValue,
  useToast,
  InputGroup,
  InputRightElement,
  HStack,
  PinInput,
  PinInputField
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { Logo } from '../components/Logo';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import axios from 'axios';
import useStore from '../store/store';

export const SigninPage = () => {
  const { setUser } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const redirectPath = searchParams.get('redirect') || '/';
  const toast = useToast();
  const [loginMethod, setLoginMethod] = useState('password');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSignin = async () => {
    if (!phone || !password) {
      toast({
        title: 'Error',
        description: 'Please enter both phone number and password',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(`${process.env.REACT_APP_SERVER}/login`, { phone, password, role: 'user' });
      if (res.status === 200) {
        setUser(res.data.user);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        toast({
          title: 'Success',
          description: 'Logged in successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        navigate(redirectPath);
      }
    } catch (error) {
      toast({
        title: 'Login failed',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!phone) {
      toast({
        title: 'Error',
        description: 'Please enter phone number',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      setLoading(true);
      const res = await axios.post(`${process.env.REACT_APP_SERVER}/send-otp`, { phone, role: 'user' });
      if (res.data.success) {
        setIsOtpSent(true);
        toast({
          title: 'OTP Sent',
          description: `OTP sent to ${phone}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        // For testing
        if (res.data.otp) console.log("TEST OTP:", res.data.otp);
      }
    } catch (error) {
      toast({
        title: 'Failed to send OTP',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) {
      toast({
        title: 'Error',
        description: 'Please enter OTP',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      setLoading(true);
      const res = await axios.post(`${process.env.REACT_APP_SERVER}/verify-otp`, { phone, otp, role: 'user' });
      if (res.status === 200) {
        setUser(res.data.user);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        toast({
          title: 'Success',
          description: 'Logged in successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        navigate(redirectPath);
      }
    } catch (error) {
      toast({
        title: 'OTP verification failed',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex
      minH={'100vh'}
      align={'center'}
      justify={'center'}
      bg={useColorModeValue('gray.50', 'gray.800')}
    >
      <Stack spacing={8} mx={'auto'} maxW={'lg'} py={12} px={6}>
        <Stack alignItems={'center'} spacing={4}>
          <Logo w="250px" />
          <Heading fontSize={'4xl'}>Sign in to your account</Heading>
          <Text fontSize={'lg'} color={'gray.600'}>
            to enjoy all of our cool <Link color={'#0C727E'}>features</Link> ✌️
          </Text>
        </Stack>
        <Box
          rounded={'lg'}
          bg={useColorModeValue('white', 'gray.700')}
          boxShadow={'lg'}
          p={8}
        >
          <Stack spacing={4}>
            <FormControl id="phone">
              <FormLabel>Phone number</FormLabel>
              <Input 
                type="tel" 
                placeholder="10 digit mobile number"
                value={phone}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setPhone(val);
                }}
                disabled={isOtpSent}
              />
            </FormControl>

            {loginMethod === 'password' ? (
              <FormControl id="password">
                <FormLabel>Password</FormLabel>
                <InputGroup>
                  <Input 
                    type={showPassword ? 'text' : 'password'} 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <InputRightElement h={'full'}>
                    <Button
                      variant={'ghost'}
                      onClick={() => setShowPassword((showPassword) => !showPassword)}
                    >
                      {showPassword ? <ViewIcon /> : <ViewOffIcon />}
                    </Button>
                  </InputRightElement>
                </InputGroup>
              </FormControl>
            ) : (
              isOtpSent && (
                <FormControl id="otp">
                  <FormLabel textAlign="center">Enter 4-Digit OTP</FormLabel>
                  <HStack justifyContent="center">
                    <PinInput 
                      otp 
                      type="number" 
                      value={otp} 
                      onChange={(value) => setOtp(value)}
                      focusBorderColor="teal.500"
                    >
                      <PinInputField />
                      <PinInputField />
                      <PinInputField />
                      <PinInputField />
                    </PinInput>
                  </HStack>
                </FormControl>
              )
            )}

            <Stack spacing={4}>
              {loginMethod === 'password' && (
                <Stack
                  direction={{ base: 'column', sm: 'row' }}
                  align={'start'}
                  justify={'space-between'}
                >
                  <Link
                    onClick={() => navigate('/forgot-password')}
                    color={'#0C727E'}
                  >
                    Forgot password?
                  </Link>
                </Stack>
              )}

              {loginMethod === 'password' ? (
                <Button
                  onClick={handleSignin}
                  color={'#fff'}
                  bg={'#1DC2D5'}
                  isLoading={loading}
                  sx={{ _hover: { color: '#fff', bgColor: '#0C727E' } }}
                >
                  Sign in with Password
                </Button>
              ) : (
                !isOtpSent ? (
                  <Button
                    onClick={handleSendOtp}
                    color={'#fff'}
                    bg={'#1DC2D5'}
                    isLoading={loading}
                    sx={{ _hover: { color: '#fff', bgColor: '#0C727E' } }}
                  >
                    Send OTP
                  </Button>
                ) : (
                  <Button
                    onClick={handleVerifyOtp}
                    color={'#fff'}
                    bg={'#1DC2D5'}
                    isLoading={loading}
                    sx={{ _hover: { color: '#fff', bgColor: '#0C727E' } }}
                  >
                    Verify & Sign in
                  </Button>
                )
              )}

              <Button
                variant="ghost"
                onClick={() => {
                  setLoginMethod(loginMethod === 'password' ? 'otp' : 'password');
                  setIsOtpSent(false);
                }}
                color={'#0C727E'}
              >
                {loginMethod === 'password' ? 'Login with OTP instead' : 'Login with Password instead'}
              </Button>
            </Stack>

            <Stack pt={0}>
              <Text align={'center'}>
                Don't have an account?{' '}
                <Link onClick={() => navigate('/signup')} color={'#0C727E'}>
                  Signup
                </Link>
              </Text>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Flex>
  );
};
